import { ErrorMessage } from "@hookform/error-message";
import { AxiosError } from "axios";
import clsx from "clsx";
import { isEqual } from "lodash-es";
import { FC } from "react";
import { FormProvider, SubmitHandler, useFieldArray, useForm } from "react-hook-form";

import { CourseScoreDisplayFormat, RubricType } from "components/server-types";
import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { LegacyButton } from "mds/components/LegacyButton";
import { CircleInfoIcon, ScoreIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type FormValues = {
  rubric: RubricType;
  course_score_display_format: CourseScoreDisplayFormat;
};

const getCourseScoreDisplayFormatDropdownValues = () => {
  return Object.values(CourseScoreDisplayFormat).map((value) => ({
    id: value,
    title: t(`home.settings.course_score_display.options.${value}`),
  }));
};

export const OrgRubricSettings: FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const currentOrg = useAppSelector(selectCurrentOrg);

  const form = useForm<FormValues>({
    defaultValues: {
      rubric: currentOrg?.rubric,
      course_score_display_format:
        currentOrg?.course_score_display_format || CourseScoreDisplayFormat.Percent,
    },
  });

  const { fields: rubricFields } = useFieldArray({
    control: form.control,
    name: "rubric",
  });

  if (!currentOrg) {
    return null;
  }

  const toastUnknownError = () => {
    toastLocalizedOperationError("failed_to_update_rubric");
  };

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    if (!isEqual(formData.rubric, currentOrg.rubric)) {
      storeApi.orgs
        .partial_update({
          id: currentOrg.id,
          rubric: formData.rubric,
        })
        .then(() => {
          toastSuccessMessage(t("success.toasts.saved_rubric"));
          trackEvent("Org Settings - Save New Rubric", BUTTON_EVENT_CATEGORY);
        })
        .catch((error: unknown) => {
          if (!(error instanceof AxiosError)) {
            toastUnknownError();
            return;
          }
          if (error.response?.status !== 400) {
            toastUnknownError();
            return;
          }
          const respData = error.response?.data as {
            input: string;
            loc: [] | ["levels", number, "name" | "score" | "conversion_percent"];
            msg: string;
            type: string;
            url?: string;
          }[];
          if (!respData) {
            toastUnknownError();
            return;
          }
          respData.forEach(({ loc, msg }) => {
            if (loc.length === 0) {
              form.setError("root", {
                type: "server",
                message: msg,
              });
            } else if (loc[0] === "levels") {
              form.setError(`rubric.${loc[1]}.${loc[2]}`, {
                type: "server",
                message: msg,
              });
            }
          });
        });
    }
    if (formData.course_score_display_format !== currentOrg.course_score_display_format) {
      storeApi.orgs
        .partial_update({
          id: currentOrg.id,
          course_score_display_format: formData.course_score_display_format,
        })
        .then(() => {
          toastSuccessMessage(t("success.toasts.updated_course_score_display_format"));
          trackEvent("Org Settings - Save New Course Score Display Format", BUTTON_EVENT_CATEGORY);
        })
        .catch(() => {
          toastUnknownError();
        });
    }
  };

  const courseScoreDisplayFormat = form.watch("course_score_display_format");

  return (
    <FormProvider {...form}>
      <form
        className="shadow-level-1 mt-2 bg-black-tint-99 px-3 py-4"
        onSubmit={!readOnly ? form.handleSubmit(onSubmit) : undefined}
      >
        <div className="h6 text-black-tint-40">
          {t("home.settings.rubric_conversion_scale.title")}
        </div>
        <div className="body-s ml-2 mt-2 text-black-tint-40">
          {t("home.settings.rubric_conversion_scale.description")}
        </div>
        <div className="body-s mt-4 flex flex-row items-center gap-2 text-black-tint-20">
          <CircleInfoIcon className="text-blue" />
          <span>{t("home.settings.rubric_conversion_scale.change_alert")}</span>
        </div>

        <div className="my-4 flex flex-col gap-4">
          <ErrorMessage name="root" />
          {rubricFields.map((field, index) => {
            // The first and last levels of the rubric have immutable conversion_percent values of 0% and 100%,
            // respectively. Interior levels have a conversion_percent value that may be edited.
            const disabled = [0, rubricFields.length - 1].includes(index);
            return (
              <div className="flex flex-row gap-2" key={field.id}>
                <ScoreIcon className="mt-0.5" value={field.score} />
                <div className="flex flex-col items-start gap-2">
                  <div className="h4 mb-1 text-black-tint-20">{field.name}</div>
                  <div
                    className={clsx(
                      "mds-text-field-icon",
                      disabled && "cursor-not-allowed bg-black-tint-90 text-black-tint-55",
                    )}
                  >
                    <input
                      key={field.id}
                      {...form.register(`rubric.${index}.conversion_percent`)}
                      className={clsx(
                        "mds-text-field mds-text-field-number text-black-tint-20",
                        disabled && "cursor-not-allowed",
                      )}
                      disabled={disabled || readOnly}
                    />
                    <span>%</span>
                  </div>
                  <ErrorMessage name={`rubric.${index}.conversion_percent`} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="h6 mt-4 text-black-tint-40">
          {t("home.settings.course_score_display.title")}
        </div>
        <div className="body-s ml-2 mt-2 text-black-tint-40">
          {t("home.settings.course_score_display.description")}
        </div>

        <DropdownMenu
          className="ml-2"
          disabled={readOnly}
          eventName="Org Settings - Course score display format selection"
          items={getCourseScoreDisplayFormatDropdownValues()}
          selectedValue={courseScoreDisplayFormat}
          onSelectionChange={({ id }) => form.setValue("course_score_display_format", id)}
        />

        <LegacyButton className="mt-4" disabled={readOnly} type="submit">
          {t("common.save")}
        </LegacyButton>
      </form>
    </FormProvider>
  );
};
